import { Calendar, Clock, Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CheckBox from 'src/components/CheckBox';
import DateSelect from 'src/components/DateSelect/DateSelect';
import DeleteModal from 'src/components/DeleteModal';
import InputMaskCmp from 'src/components/InputMaskCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { setConsigneeOptions, setConsignees } from 'src/redux/address.slice';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { setCreateOrderData } from 'src/redux/CreateOrders.slice';
import { isViewQuoteData } from 'src/redux/Quoting.slice';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import { logError } from 'src/services/LoggingService';
import { createDateWithoutTimezoneConversion, formatTimeInput, getDateWithSuffixFormat, getFormattedDateIgnoringTime, isShipperConsigneeAddressValid } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { DATE_TYPE, DELIVERY_DATE_TYPE_OPTIONS, DELIVERY_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from '../AddQuote/Steps/addQuoteSteps.const';
import { DELIVERY_WINDOW_TOOLTIP } from '../CreateQuote/Steps/Steps.const';
import { IAction, ILimitedAccessDeliveryData } from '../QuoteDetail/quote.interface';
import { consigneeFormKeys, initAction } from '../quoteDetail.const';

import AdditionalServiceModal from './AdditionalServiceModal';
import EditShipperConsigneeForm from './EditShipperConsigneeForm';
import LimitedAccessDeliveryModal from './LimitedAccessDeliveryModal';

const ConsigneeDetails = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isViewQuote = useSelector(isViewQuoteData);
    const appearance = useSelector(appearanceSelector);

    const { isServiceLoading = false, serviceTypes = { PICKUP: [], DELIVERY: [] }, consigneeOptions = [], consignees = [], handleSearchConsignee } = useAddressDetails();

    const [action, setAction] = useState<IAction>(initAction);
    const [isEditConsigneeId, setIsEditConsigneeId] = useState(false);
    const [preEditId, setPreEditId] = useState<number | null>(null);
    const [isOpenFromAddNewConsignee, setIsOpenFromAddNewConsignee] = useState<boolean>(false);
    const [deleteAction, setDeleteAction] = useState({
        mode: '',
        isContact: false,
        contactId: null as number | null,
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const savedDeliveryEndDateRef = useRef<string | null>(null);

    const {
        control,
        setValue,
        watch,
        getValues,
        clearErrors,
        trigger,
        formState: { errors, isSubmitted },
    } = useFormContext();

    const selectedServices = watch('serviceId');
    const deliverySensitivity = watch('deliverySensitivity');
    const deliveryDate = watch('deliveryDate');
    const deliveryWindowType = watch('deliveryWindowType');
    const pickupDate = watch('pickupDate');

    const watchConsigneeCompanyName = watch('consigneeCompanyName');
    const watchConsigneeAddress = watch('consigneeAddress');
    const watchConsigneeFullAddress = watch('consigneeAddress')?.fullAddress;
    const watchConsigneeId = watch('consigneeId');

    const selectedConsigneeBusinessHours = useMemo(() => {
        const entity = consignees?.find((c: any) => c.id === watchConsigneeId);
        const start = entity?.businessHoursStartTime ? String(entity.businessHoursStartTime).slice(0, 5) : null;
        const end = entity?.businessHoursEndTime ? String(entity.businessHoursEndTime).slice(0, 5) : null;

        return start && end ? `${start} – ${end}` : null;
    }, [watchConsigneeId, consignees]);

    const handleAddress = (key: string, value: any) => {
        const mapping = consigneeFormKeys;
        Object.entries(mapping).forEach(([formKey, dataKey]) => {
            setValue(formKey, value[dataKey]);
        });
    };

    const handleAction =
        (type: string, isOpen: boolean = true, mode: any = '') =>
        () => {
            setAction((old) => ({ ...old, [type]: isOpen, mode }));
        };

    const handleServiceChange = (e: any) => {
        let { value, checked } = e.target;
        value = parseInt(value);

        const currentServices = Array.isArray(selectedServices) ? selectedServices : [];

        if (checked) {
            if (e.target.name === 'limited_access_delivery') {
                handleAction('limitedAccess')();
            } else {
                setValue('serviceId', [...currentServices, value]);
            }
        } else {
            const filteredServices = currentServices.filter((item: any) => item !== value);
            setValue('serviceId', filteredServices);

            if (e.target.name === 'limited_access_delivery') {
                setValue('limitedAccess', false);
                setValue('limitedAccessOther', '');
                setValue('limitedAccessType', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'limited_access_delivery') {
            handleAction('limitedAccess')();
        }
    };

    const getList = (data: any) => (
        <div className="flex flex-wrap -m-1">
            {data.map((val: any) => (
                <div className="flex p-1 w-1/3" key={`service_${val.id}`}>
                    <CheckBox
                        id={`service_${val.id}`}
                        onChangeFunc={handleServiceChange}
                        name={val.slug}
                        value={val.id}
                        checked={selectedServices?.includes(val.id) || false}
                        parentClassName="mt-px"
                        disabled={val.isOnlyForFTL || isViewQuote}
                    />
                    <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-1.5 flex-1">
                        {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                            <TooltipCmp message={val.name} childrenClassName="!flex">
                                <label
                                    htmlFor={`service_${val.id}`}
                                    className={`leading-4 text-textSecondary text-xs font-medium truncate ${isViewQuote ? '!cursor-not-allowed' : ''} ${
                                        val.isOnlyForFTL ? 'line-through' : 'cursor-pointer'
                                    }`}
                                >
                                    {val.slug === 'pickup_appointment_required'
                                        ? t('QuotingHub.Common.Pickup_Appointment')
                                        : val.slug === 'delivery_appointment_required'
                                        ? t('QuotingHub.Common.Delivery_Appointment')
                                        : t('QuotingHub.Common.After_Hours_Pickup')}
                                </label>
                            </TooltipCmp>
                        ) : (
                            <label
                                htmlFor={`service_${val.id}`}
                                className={`leading-4 text-textSecondary text-xs font-medium truncate ${isViewQuote ? '!cursor-not-allowed' : ''} ${
                                    val.isOnlyForFTL ? 'line-through' : 'cursor-pointer'
                                }`}
                            >
                                {val.slug === 'after_hours_pickup' ? t('QuotingHub.Common.After_Hours_Pickup') : val.name}
                            </label>
                        )}
                        {selectedServices?.includes(val.id) && val.slug === 'limited_access_delivery' ? (
                            <TooltipCmp message={t('Common.Edit')}>
                                <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                            </TooltipCmp>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        setValue('limitedAccess', true);
        setValue('limitedAccessOther', data.limitedAccessOther);
        setValue('limitedAccessType', data.selectedLimitedAccessType);
        const limitedAccessId = serviceTypes.DELIVERY.find((val: any) => val.slug === 'limited_access_delivery')?.id;
        const currentServiceId = getValues('serviceId') || [];
        const updatedServiceId = currentServiceId.includes(limitedAccessId) ? currentServiceId : [...currentServiceId, limitedAccessId];
        setValue('serviceId', updatedServiceId);
    };

    const resetConsigneeForm = () => {
        setValue('consigneeCompanyName', '');
        setValue('consigneeAddress', null);
        setValue('consigneeFullAddress', '');
        setValue('consigneeAddress1', null);
        setValue('consigneeStreetNumber', null);
        setValue('consigneeCity', null);
        setValue('consigneeState', null);
        setValue('consigneeCountry', null);
        setValue('consigneePostal', null);
        setValue('consigneeLatitude', null);
        setValue('consigneeLongitude', null);
        setValue('consigneeStateCode', null);
        setValue('consigneeCountryCode', null);
    };

    const handleCreateNewConsignee = () => {
        setIsEditConsigneeId(true);
        setValue('consigneeId', null);
        setValue('consigneeCompanyName', '');
        setValue('consigneeAddress', null);
        clearErrors('addressMismatch' as any);
        setIsOpenFromAddNewConsignee(true);
    };

    const handleSwitchConsignee = () => {
        setValue('consigneeId', null);
        resetConsigneeForm();
        clearErrors('addressMismatch' as any);
    };

    const handleCardActionTypeConsignee = (value: string) => () => {
        if (value === 'edit_id') {
            setPreEditId(watchConsigneeId ? +watchConsigneeId : null);
            setIsEditConsigneeId(true);
        } else if (value === 'delete_id') {
            setDeleteAction({ mode: 'delete', isContact: false, contactId: null });
        } else if (value === 'switch_id') {
            handleSwitchConsignee();
        }
    };

    const handleEditConsignee = (isEdit?: boolean) => () => {
        setIsEditConsigneeId(isEdit ?? true);

        if (!isEdit) {
            const isValidId = preEditId && preEditId === watchConsigneeId;

            if (isValidId) {
                setPreEditId(null);
            } else {
                setValue('consigneeId', null);
                resetConsigneeForm();
            }
        }
    };

    const handleSaveConsignee = (data: any) => {
        setIsEditConsigneeId(false);
        setValue('consigneeId', data.id);
        setValue('consigneeCompanyName', data.companyName);
        setValue('consigneeAddress', data.address || data);

        if (data.address || data) {
            handleAddress('consignee', data.address || data);
        }
        setPreEditId(null);
        clearErrors('addressMismatch' as any);
    };

    const handleGoogleAddressSelect = (addressValue: any) => {
        const addressObject = {
            fullAddress: addressValue?.fullAddress ?? '',
            address1: addressValue?.address1 ?? '',
            city: addressValue?.city ?? '',
            country: addressValue?.country ?? '',
            countryCode: addressValue?.countryCode ?? '',
            latitude: addressValue?.latitude ?? '',
            longitude: addressValue?.longitude ?? '',
            postal: addressValue?.postal ?? '',
            state: addressValue?.state ?? '',
            stateCode: addressValue?.stateCode ?? '',
            streetNumber: addressValue?.streetNumber ?? '',
            timezone: addressValue?.timezone ?? '',
            additionalServiceIds: addressValue?.additionalServiceIds ?? [],
            companyName: '',
        };
        setValue('consigneeId', null);
        setValue('consigneeAddress', addressObject);
        handleAddress('consignee', addressObject);
        clearErrors('addressMismatch' as any);
        setIsEditConsigneeId(true);
    };

    const handleConsigneeChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            const data = consignees.find((consigneeData: any) => consigneeData.id === value.value);

            if (data) {
                setValue('consigneeCompanyName', data.companyName);
                setValue('consigneeAddress', data);
                handleAddress('consignee', data);
                clearErrors('addressMismatch' as any);

                if (!isShipperConsigneeAddressValid(data)) {
                    setIsEditConsigneeId(true);
                }
            }
        } else {
            onChange(null);
            resetConsigneeForm();
        }
    };

    const handleModalClose = (closeStatus: boolean) => () => {
        setDeleteAction({ mode: '', isContact: false, contactId: null });

        if (closeStatus) {
            setIsDeleting(true);
            const idToDelete = watchConsigneeId ? +watchConsigneeId : null;

            if (idToDelete) {
                deleteSingleAddress(idToDelete.toString())
                    .then((result: any) => {
                        if (result) {
                            resetConsigneeForm();
                            setValue('consigneeId', null);

                            const updatedConsignees = consignees.filter((consignee: any) => consignee.id !== idToDelete);
                            const updatedConsigneeOptions = consigneeOptions.filter((option: any) => option.value !== idToDelete);
                            dispatch(setConsignees(updatedConsignees));
                            dispatch(setConsigneeOptions(updatedConsigneeOptions));
                            dispatch(setCreateOrderData({ data: { consigneeDetails: null } }));

                            setIsEditConsigneeId(false);
                            WalToast.success(t('ValidationMessages.Consignee_removed'));
                        }
                    })
                    .catch(async (error: any) => {
                        const errorMessage = error?.response?.data?.message || t('ValidationMessages.Consignee_delete_failed');
                        WalToast.error(errorMessage);

                        await logError(error, {
                            errorMessage: error?.message || 'Error while deleting consignee',
                            errorLocation: 'ConsigneeDetails: handleModalClose',
                            storeInDatabase: true,
                            logToConsole: true,
                        });
                    })
                    .finally(() => {
                        setIsDeleting(false);
                    });
            } else {
                setIsDeleting(false);
            }
        }
    };

    useEffect(() => {
        if (watchConsigneeId) {
            const currentConsigneeLatitude = getValues('consigneeLatitude');

            if (watchConsigneeAddress && currentConsigneeLatitude) {
                return;
            }

            const data = consignees.find((consigneeData: any) => consigneeData.id === watchConsigneeId);

            if (data) {
                setValue('consigneeCompanyName', data.companyName);
                setValue('consigneeAddress', data);
                handleAddress('consignee', data);
                clearErrors('addressMismatch' as any);
            }
        } else {
            const currentConsigneeLatitude = getValues('consigneeLatitude');

            if (!currentConsigneeLatitude && !watchConsigneeAddress) {
                resetConsigneeForm();
            }
        }
    }, [consignees, watchConsigneeId]);

    useEffect(() => {
        if (watchConsigneeAddress?.fullAddress) {
            const isValid = isShipperConsigneeAddressValid(watchConsigneeAddress);

            if (!isValid || !watchConsigneeId) {
                setIsEditConsigneeId(true);
            }
        }
    }, []);

    // Delivery field handlers
    const getMinimumDate = useCallback(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone)), [appearance?.timezone]);

    const getMinimumDeliveryDate = useCallback(() => {
        if (pickupDate) {
            // Use moment() not moment.utc() — UTC on a local Date object shifts the date in non-UTC timezones
            const todayStr = moment.tz(appearance?.timezone).format('YYYY-MM-DD');
            const pickupStr = moment(pickupDate).format('YYYY-MM-DD');

            // min delivery = max(today, pickupDate) — guard against stale past pickup date
            return moment(pickupStr).isSameOrAfter(todayStr, 'day') ? createDateWithoutTimezoneConversion(moment(pickupDate)) : getMinimumDate();
        }

        return getMinimumDate();
    }, [pickupDate, getMinimumDate, appearance?.timezone]);

    const handleDeliverySensitivityChange = (val: string, onChange: any) => () => {
        const currentEndDate = getValues('deliveryEndDate');
        onChange(val);

        if (val !== DATE_TYPE.FLEXIBLE) {
            savedDeliveryEndDateRef.current = currentEndDate ?? null;
            setValue('deliveryEndDate', null);

            if (isSubmitted) clearErrors(['deliveryEndDate', 'deliveryDate']);
        } else {
            const restoredEnd = savedDeliveryEndDateRef.current;

            if (restoredEnd) {
                setValue('deliveryEndDate', restoredEnd);
                savedDeliveryEndDateRef.current = null;
            }
        }
    };

    const handleDeliveryDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);

        const currentDeliveryEndDate = watch('deliveryEndDate');

        if (currentDeliveryEndDate && moment.utc(value).isAfter(moment.utc(currentDeliveryEndDate), 'day')) {
            setValue('deliveryEndDate', null);

            if (isSubmitted) {
                trigger('deliveryEndDate');
            }
        }
    };

    const handleDeliveryWindowTypeChange = (val: string, onChange: any) => () => {
        onChange(val);

        if (val === 'open') {
            setValue('deliveryStartTime', null);
            setValue('deliveryEndTime', null);
        } else if (val === 'appointment') {
            setValue('deliveryEndTime', null);
        }

        if (isSubmitted) {
            trigger('deliveryStartTime');
            trigger('deliveryEndTime');
        }
    };

    const handleTimeInputChange = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void, previousValue: string) => {
        const formattedTime = formatTimeInput(event.target.value, previousValue);
        onChange(formattedTime);
    };

    return (
        <>
            {/* Consignee Details */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">
                    {t('Common.Consignee_Details')}
                    <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                </h6>
                <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 flex flex-wrap gap-x-2 space-y-3">
                    <ShipperConsigneeAutocomplete
                        control={control}
                        isShipper={false}
                        isEditId={isEditConsigneeId}
                        watchCompanyName={watchConsigneeCompanyName}
                        watchShipperAddress={watchConsigneeAddress}
                        watchShipperFullAddress={watchConsigneeFullAddress}
                        shipperOptions={[]}
                        consigneeOptions={consigneeOptions}
                        errors={errors}
                        handleShipperChange={handleConsigneeChange}
                        handleCreateNew={handleCreateNewConsignee}
                        handleCardActionType={handleCardActionTypeConsignee}
                        fieldName="consigneeId"
                        isAddressLoading={!!watchConsigneeAddress && !watchConsigneeFullAddress}
                        showDeleteOption={!!watchConsigneeId}
                        handleGoogleAddressSelect={handleGoogleAddressSelect}
                        watchShipperConsigneeId={watchConsigneeId && consignees.find((consignee) => consignee.id === watchConsigneeId) ? watchConsigneeId : null}
                        onInputChange={(e: any, { action: inputAction }: any) => {
                            if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
                                handleSearchConsignee(e);
                            }
                        }}
                    />
                    {isEditConsigneeId && (
                        <EditShipperConsigneeForm
                            handleEditShipper={handleEditConsignee}
                            id={
                                isOpenFromAddNewConsignee
                                    ? undefined
                                    : consignees.find((consignee) => Number(consignee.id) === Number(watchConsigneeId))
                                    ? watchConsigneeId
                                        ? +watchConsigneeId
                                        : undefined
                                    : undefined
                            }
                            handleSaveShipper={handleSaveConsignee}
                            isShipper={false}
                            initialCompanyName={watchConsigneeCompanyName}
                            initialAddress={watchConsigneeAddress}
                        />
                    )}

                    {/* Delivery Additional Services */}
                    <div className="space-y-1.5 w-full">
                        <div className="flex items-center mb-1.5">
                            <h6 className="form_label block mb-0">{t('QuotingHub.Common.Delivery_Additional_Services')}</h6>
                            <div className={`cursor-pointer ${isServiceLoading ? 'custom-loading' : ''}`} onClick={handleAction('delivery')}>
                                <TooltipCmp message={t('QuotingHub.Common.Delivery_Additional_Services')}>
                                    <InfoCircle className="w-3.5 h-3.5 ml-1.5 text-primary cursor-pointer" />
                                </TooltipCmp>
                            </div>
                        </div>
                        <div className="rounded-lg border border-utilityGray200 bg-white shadow-sm p-3">
                            {!isServiceLoading && serviceTypes.DELIVERY?.length ? (
                                getList(serviceTypes.DELIVERY)
                            ) : isServiceLoading ? (
                                <div className="flex flex-wrap -m-1">
                                    {Array.from({ length: 12 }).map((_, index) => (
                                        <div className="flex p-1 w-1/3" key={`service_${index}`}>
                                            <CheckBox id={`service_abc`} onChangeFunc={handleServiceChange} name="abc" value="xyz" checked={false} parentClassName="mt-0.5 custom-loading" />
                                            <div className="flex leading-4">
                                                <label htmlFor={`service_abc`} className="md:ml-2 ml-1.5 text-sm leading-4 text-textSecondary font-medium xxl:me-2.5 me-1.5 custom-loading">
                                                    Test Service Abc
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <NotFoundUI
                                    iconClassName="!hidden"
                                    title={t('QuotingHub.QuotingDashboard.No_Services_Found')}
                                    desc={t('QuotingHub.QuotingDashboard.No_delivery_Services_found_desc')}
                                    containerClassName="!h-auto !my-5"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                {/* Delivery Sensitivity + Est. Delivery Date */}
                <div className="flex gap-x-3 items-end">
                    <Controller
                        name="deliverySensitivity"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <SelectBox
                                label={t('QuotingHub.CreateQuote.Delivery_Sensitivity')}
                                labelClassName="form_label block mb-1.5"
                                parentClassName="w-full"
                                options={DELIVERY_DATE_TYPE_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
                                value={value ? DELIVERY_DATE_TYPE_OPTIONS.find((option) => value === option.value) : null}
                                onChangeFunc={(selectedOption: any) => {
                                    handleDeliverySensitivityChange(selectedOption?.value, onChange)();
                                }}
                                className="w-full"
                                isError={!!error}
                                isDisabled={isViewQuote}
                                placeholder={t('QuotingHub.CreateQuote.Delivery_Sensitivity')}
                            />
                        )}
                    />

                    <Controller
                        name="deliveryDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                labelClassName="form_label block mb-1.5"
                                inputName="deliveryDate"
                                className="form_control w-full"
                                placeholder={deliverySensitivity === 'flexible' ? t('Common.Start_date') : t('Common.Select_delivery_date')}
                                dateFormat="dd/MM/yyyy"
                                minDate={getMinimumDeliveryDate()}
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                onChangeFunc={handleDeliveryDateChange(onChange)}
                                errorText={error?.message ? true : false}
                                icon={<Calendar className="h-5 w-5" />}
                                parentClassName="w-full one-month-datepicker"
                                dateSelectClassName={isViewQuote || deliverySensitivity === 'open' ? 'pointer-events-none' : ''}
                                ReactDatePickerSelectWrapClass={isViewQuote || deliverySensitivity === 'open' ? '!cursor-not-allowed' : ''}
                                disabled={isViewQuote || deliverySensitivity === 'open'}
                            />
                        )}
                    />
                    {/* Delivery End Date — flexible only */}
                    {deliverySensitivity === 'flexible' && (
                        <Controller
                            name="deliveryEndDate"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    labelClassName="form_label block mb-1.5"
                                    inputName="deliveryEndDate"
                                    className="form_control w-full"
                                    placeholder={t('Common.End_date')}
                                    dateFormat="dd/MM/yyyy"
                                    minDate={deliveryDate ? createDateWithoutTimezoneConversion(moment(deliveryDate)) : getMinimumDeliveryDate()}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    onChangeFunc={(event: any) => {
                                        onChange(event.value);

                                        if (isSubmitted) {
                                            clearErrors('deliveryEndDate');
                                            trigger('deliveryDate');
                                        }
                                    }}
                                    errorText={!!error}
                                    icon={<Calendar className="h-5 w-5" />}
                                    parentClassName="w-full one-month-datepicker"
                                    dateSelectClassName={isViewQuote ? 'pointer-events-none' : ''}
                                    ReactDatePickerSelectWrapClass={isViewQuote ? '!cursor-not-allowed' : ''}
                                    disabled={isViewQuote}
                                />
                            )}
                        />
                    )}
                </div>
            </div>
            <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                <div className="flex flex-col w-full">
                    {/* Delivery Window */}
                    <div className="flex gap-x-3 items-end">
                        <Controller
                            name="deliveryWindowType"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <div className="flex-1">
                                    <div className="flex items-center mb-1.5">
                                        <span className="form_label !mb-0">{t('BookOrder.Delivery_Window')}</span>
                                    </div>
                                    <SelectBox
                                        parentClassName="w-full"
                                        options={DELIVERY_WINDOW_TYPE_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
                                        value={value ? DELIVERY_WINDOW_TYPE_OPTIONS.find((option) => value === option.value) : null}
                                        onChangeFunc={(selectedOption: any) => {
                                            handleDeliveryWindowTypeChange(selectedOption?.value, onChange)();
                                        }}
                                        className="w-full"
                                        isError={!!error}
                                        isDisabled={isViewQuote}
                                        placeholder={t('QuotingHub.Common.Select_Delivery_Window')}
                                    />
                                </div>
                            )}
                        />

                        {/* Start Time */}
                        {deliveryWindowType !== 'open' && (
                            <Controller
                                name="deliveryStartTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={`flex-1 ${isViewQuote ? 'pointer-events-none' : ''}`}>
                                        <InputMaskCmp
                                            inputName="deliveryStartTime"
                                            placeholder={t('Orders.Order_Stepper.Shipper_Details.HH_MM')}
                                            parentClassName="flex-1"
                                            value={(value as string) ?? ''}
                                            HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                            className={error?.message ? 'border border-red-500 border-solid' : ''}
                                            mask="99:99"
                                            icon={
                                                <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                    <Clock className="h-4 w-4" />
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            />
                        )}

                        {/* End Time — ready_time only */}
                        {deliveryWindowType === 'delivery_time' && (
                            <Controller
                                name="deliveryEndTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={`flex-1 ${isViewQuote ? 'pointer-events-none' : ''}`}>
                                        <InputMaskCmp
                                            inputName="deliveryEndTime"
                                            placeholder={t('Orders.Order_Stepper.Shipper_Details.HH_MM')}
                                            parentClassName="flex-1"
                                            value={(value as string) ?? ''}
                                            HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                            mask="99:99"
                                            isError={!!error}
                                            icon={
                                                <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                    <Clock className="h-4 w-4" />
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            />
                        )}
                    </div>

                    {DELIVERY_WINDOW_TOOLTIP[deliveryWindowType] && (
                        <span className="mt-1.5 text-[12px] font-normal leading-[18px] text-grayLight600 italic">
                            {deliveryWindowType === WINDOW_TYPE.OPEN && selectedConsigneeBusinessHours
                                ? DELIVERY_WINDOW_TOOLTIP[deliveryWindowType].replace('business hours', `business hours (${selectedConsigneeBusinessHours})`)
                                : DELIVERY_WINDOW_TOOLTIP[deliveryWindowType]}
                        </span>
                    )}
                </div>
            </div>
            {/* Modals */}
            {action.delivery && (
                <AdditionalServiceModal handleClose={handleAction('delivery', false)} services={serviceTypes?.DELIVERY || []} title={t('QuotingHub.Common.Delivery_Additional_Services')} />
            )}
            {action.limitedAccess && (
                <LimitedAccessDeliveryModal
                    handleClose={handleAction('limitedAccess', false)}
                    handleLimitedAccess={handleLimitedAccess}
                    defaultFormValues={{
                        limitedAccessType: watch('limitedAccessType') || '',
                        limitedAccessOther: watch('limitedAccessOther') || '',
                    }}
                />
            )}
            {deleteAction.mode === 'delete' && (
                <DeleteModal
                    handleClose={handleModalClose}
                    moduleTitle={t('Orders.remove_from_wal_hq', { companyName: watchConsigneeCompanyName || '' })}
                    moduleName={t('Common.Consignee')}
                    isDeleteLoading={isDeleting}
                />
            )}
        </>
    );
};

export default ConsigneeDetails;
